import { isString, isArray } from 'lodash-es'
import * as regex_ from '#app/lib/regex'
import typeOf from '#app/lib/type_of'
import type { AbsoluteUrl, RelativeUrl } from '#server/types/common'
import type { CouchUuid } from '#server/types/couchdb'
import type { EntityUri, ExtendedEntityUri, IsbnEntityUri, PropertyUri, WdEntityUri, WdPropertyUri } from '#server/types/entity'
import type { AssetImagePath, ImageDataUrl, GroupImagePath, ImageHash, ImagePath, UserImagePath } from '#server/types/image'
import type { UserAccountUri } from '#server/types/server'
import type { Email, Username } from '#server/types/user'
import type { ItemId, PropertyId } from 'wikibase-sdk'

function bindedTest <T> (regexName: keyof typeof regex_) {
  return function (str: unknown): str is T {
    // eslint-disable-next-line import-x/namespace
    return typeof str === 'string' && regex_[regexName].test(str)
  }
}

export function isNonEmptyString (str: unknown): str is string {
  return isString(str) && (str.length > 0)
}

// TODO: rename to isAbsoluteUrl, for consistency with the the types
export function isUrl (url: unknown): url is AbsoluteUrl {
  if (typeof url !== 'string') return false
  try {
    const { protocol, username, password } = new URL(url)
    if (!(protocol === 'http:' || protocol === 'https:')) return false
    if (username !== '' || password !== '') return false
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    // On the server, only err.code === 'ERR_INVALID_URL' returns false
    // and other errors are rethrown, but in the browser
    // err.code might be inconsistent
    // Ex: in Firefox 97, the error is "TypeError: URL constructor: bla is not a valid URL."
    return false
  }
  return true
}

export function isRelativeUrl (url: unknown): url is RelativeUrl {
  if (typeof url !== 'string') return
  return url.startsWith('/')
}

export const isCouchUuid = bindedTest<CouchUuid>('CouchUuid')
export const isImageHash = bindedTest<ImageHash>('ImageHash')
export const isLocalImg = bindedTest<ImagePath>('LocalImg')
export const isAssetImg = bindedTest<AssetImagePath>('AssetImg')
export const isUserImg = bindedTest<UserImagePath>('UserImg')
export const isGroupImg = bindedTest<GroupImagePath>('GroupImg')
export const isInvEntityId = isCouchUuid
export const isEmail = bindedTest<Email>('Email')
export const isUserId = isCouchUuid
export const isGroupId = isCouchUuid
export const isItemId = isCouchUuid
export const isShelfId = isCouchUuid
export const isUsername = bindedTest<Username>('Username')
export const isEntityUri = bindedTest<EntityUri>('EntityUri')
export const isExtendedEntityUri = bindedTest<ExtendedEntityUri>('ExtendedEntityUri')
export const isIsbnEntityUri = bindedTest<IsbnEntityUri>('IsbnEntityUri')
export const isPropertyUri = bindedTest<PropertyUri>('PropertyUri')
export const isWikidataItemId = bindedTest<ItemId>('WikidataItemId')
export const isWikidataPropertyId = bindedTest<PropertyId>('WikidataPropertyId')
export const isWikidataItemUri = bindedTest<WdEntityUri>('WikidataItemUri')
export const isWikidataPropertyUri = bindedTest<WdPropertyUri>('WikidataPropertyUri')

export const isNonNull = obj => obj != null

export const isNonEmptyArray = array => isArray(array) && (array.length > 0)

export function isPlainObject (obj: unknown): obj is Exclude<object, null | unknown[]> {
  return typeOf(obj) === 'object'
}

export const isNonEmptyPlainObject = obj => isPlainObject(obj) && Object.keys(obj).length > 0

export const isPositiveIntegerString = str => isString(str) && /^\d*$/.test(str)
export const isStrictlyPositiveIntegerString = str => isString(str) && /^[1-9]\d*$/.test(str)

export function isImageDataUrl (str: string): str is ImageDataUrl {
  return /^data:image/.test(str)
}

export const isDateString = dateString => {
  if ((dateString == null) || (typeof dateString !== 'string')) return false
  return /^-?\d{4}(-\d{2})?(-\d{2})?$/.test(dateString)
}

// As Svelte components are the only known users of CustomEvent
// in the stack, it doesn't not need to be more specific than that for now
export function isComponentEvent (obj): obj is CustomEvent {
  return obj instanceof CustomEvent
}

export function isDOMEvent (obj): obj is Event {
  return obj instanceof Event
}

function isHost (str: string) {
  try {
    const { host } = new URL(`http://${str}`)
    return str === host
  } catch (err) {
    if (err.code !== 'ERR_INVALID_URL') throw err
    return false
  }
}

export function isUserAcct (str: unknown): str is UserAccountUri {
  if (typeof str !== 'string') return false
  const [ handle, host ] = str.split('@')
  return isUserId(handle) && isHost(host)
}
